import React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';
import { getMyCoinsApi } from '@/api/vendo';
import { getAdminCoins } from '@/api/admin';

// "View all coins today" for the coin ticker. Searchable + paginated. The
// operator view lists their own devices' coins; the admin view is fleet-wide
// with operator + type columns. Opened via useUIStore 'coins-all'.
const CoinsAllModal = ({
   data,
}: {
   data: { source: 'operator' | 'admin' };
}) => {
   const isAdmin = data?.source === 'admin';
   const [q, setQ] = useState('');
   const [debounced, setDebounced] = useState('');
   const [page, setPage] = useState(1);

   // Debounce the search box so each keystroke doesn't hit the API.
   useEffect(() => {
      const t = setTimeout(() => {
         setDebounced(q.trim());
         setPage(1);
      }, 300);
      return () => clearTimeout(t);
   }, [q]);

   const { data: res, isFetching } = useQuery({
      queryKey: ['coins-all', data?.source, debounced, page],
      queryFn: () =>
         isAdmin ? getAdminCoins(debounced, page) : getMyCoinsApi(debounced, page),
      placeholderData: keepPreviousData,
   });

   const coins = res?.coins ?? [];
   const total = res?.total ?? 0;
   const limit = res?.limit ?? 20;
   const pageCount = Math.max(1, Math.ceil(total / limit));
   const fmtTime = (iso: string) =>
      new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
   const typeLabel = (c: { type?: string; vlanId?: number | null }) =>
      c.type === 'main'
         ? 'Main'
         : c.vlanId != null
         ? `Sub · VLAN ${c.vlanId}`
         : 'Sub';

   return (
      <Wrap>
         <SearchRow>
            <Search
               autoFocus
               value={q}
               placeholder={
                  isAdmin
                     ? 'Search MAC, device, or operator…'
                     : 'Search customer MAC or device…'
               }
               onChange={(e) => setQ(e.target.value)}
            />
            <Count>
               {total} coin{total === 1 ? '' : 's'} today
               {isFetching ? ' · …' : ''}
            </Count>
         </SearchRow>

         <TableScroll>
            <Table>
               <thead>
                  {isAdmin ? (
                     <tr>
                        <Th>Time</Th>
                        <Th>Customer MAC</Th>
                        <Th $right>Amount</Th>
                        <Th>Device</Th>
                        <Th>Type</Th>
                        <Th>Operator</Th>
                        <Th>ISP</Th>
                        <Th />
                     </tr>
                  ) : (
                     <tr>
                        <Th>Time</Th>
                        <Th>Device</Th>
                        <Th>Customer MAC</Th>
                        <Th $right>Minutes</Th>
                        <Th $right>Amount</Th>
                        <Th />
                     </tr>
                  )}
               </thead>
               <tbody>
                  {coins.length === 0 && (
                     <tr>
                        <Td colSpan={isAdmin ? 8 : 6}>
                           <Empty>
                              {debounced
                                 ? 'No coins match your search.'
                                 : 'No coins yet today.'}
                           </Empty>
                        </Td>
                     </tr>
                  )}
                  {coins.map((c) =>
                     isAdmin ? (
                        <tr key={c.id}>
                           <Td>{fmtTime(c.at)}</Td>
                           <Td>
                              <Mac>{c.mac || '—'}</Mac>
                           </Td>
                           <Td $right>
                              <b>{currencyFormat(c.amount, false)}</b>
                           </Td>
                           <Td>{c.deviceName}</Td>
                           <Td>{typeLabel(c as { type?: string; vlanId?: number | null })}</Td>
                           <Td>{(c as { operator?: string }).operator ?? '—'}</Td>
                           <Td>{(c as { isp?: string | null }).isp || '—'}</Td>
                           <Td>
                              {c.kind === 'new' ? (
                                 <Badge $new>New</Badge>
                              ) : c.kind === 'extend' ? (
                                 <Badge>Extend</Badge>
                              ) : null}
                           </Td>
                        </tr>
                     ) : (
                        <tr key={c.id}>
                           <Td>{fmtTime(c.at)}</Td>
                           <Td>{c.deviceName}</Td>
                           <Td>
                              <Mac>{c.mac || '—'}</Mac>
                           </Td>
                           <Td $right>{c.minutes > 0 ? `+${c.minutes}m` : '—'}</Td>
                           <Td $right>
                              <b>{currencyFormat(c.amount, false)}</b>
                           </Td>
                           <Td>
                              {c.kind === 'new' ? (
                                 <Badge $new>New</Badge>
                              ) : c.kind === 'extend' ? (
                                 <Badge>Extend</Badge>
                              ) : null}
                           </Td>
                        </tr>
                     )
                  )}
               </tbody>
            </Table>
         </TableScroll>

         {pageCount > 1 && (
            <Pager>
               <PageBtn disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  ‹ Prev
               </PageBtn>
               <PageInfo>
                  Page {page} of {pageCount}
               </PageInfo>
               <PageBtn
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
               >
                  Next ›
               </PageBtn>
            </Pager>
         )}
      </Wrap>
   );
};

export default CoinsAllModal;

const Wrap = styled.div`
   width: min(940px, 92vw);
   display: flex;
   flex-direction: column;
   gap: 12px;
`;
const SearchRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;
const Search = styled.input`
   flex: 1;
   padding: 9px 12px;
   font-size: 14px;
   border: 1px solid ${colors.GRAY14};
   border-radius: 8px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;
const Count = styled.span`
   font-size: 13px;
   color: ${colors.GRAY3};
   white-space: nowrap;
`;
const TableScroll = styled.div`
   overflow-x: auto;
   max-height: 52vh;
   overflow-y: auto;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;
const Th = styled.th<{ $right?: boolean }>`
   position: sticky;
   top: 0;
   background: ${colors.WHITE};
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   font-size: 11px;
   letter-spacing: 0.03em;
   text-transform: uppercase;
   color: ${colors.GRAY3};
   padding: 6px 10px;
   border-bottom: 1px solid ${colors.GRAY14};
   white-space: nowrap;
`;
const Td = styled.td<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   padding: 8px 10px;
   border-bottom: 1px solid ${colors.GRAY14};
   white-space: nowrap;
   color: ${colors.MAIN_DARK};
`;
const Mac = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   color: ${colors.GRAY3};
`;
const Badge = styled.span<{ $new?: boolean }>`
   font-size: 11px;
   font-weight: 700;
   padding: 2px 7px;
   border-radius: 999px;
   color: ${({ $new }) => ($new ? colors.SUCCESS : colors.PRIMARY)};
   background: ${({ $new }) =>
      $new ? `${colors.SUCCESS}1f` : `${colors.PRIMARY}1f`};
`;
const Empty = styled.div`
   padding: 22px 6px;
   font-size: 13px;
   color: ${colors.GRAY3};
   text-align: center;
`;
const Pager = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 12px;
`;
const PageBtn = styled.button`
   padding: 6px 12px;
   font-size: 13px;
   border: 1px solid ${colors.GRAY14};
   background: ${colors.WHITE};
   border-radius: 8px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: default;
   }
`;
const PageInfo = styled.span`
   font-size: 13px;
   color: ${colors.GRAY3};
`;
